import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { thesisGet, thesisGenerateContra, thesisRefreshRun, thesisGeneratePlan, thesisWaitPlanDone } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ThesisResult from "@/components/thesis/ThesisResult";
import TendenciaResult from "@/components/thesis/TendenciaResult";
import RefreshButton from "@/components/RefreshButton";
import SharedGeneration from "@/components/thesis/SharedGeneration";

export default function ThesisDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [thesis, setThesis] = useState(null);
    // Coming straight from the generator: what changed against the previous thesis
    // (only the generation response has it), kept on top of every reload of this page.
    const genNotes = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generatingContra, setGeneratingContra] = useState(false);
    const [generatingPlan, setGeneratingPlan] = useState(false);

    useEffect(() => {
        let alive = true;
        const st = location.state;
        genNotes.current = st && (st.changes?.length || st.no_changes) ? { changes: st.changes || [], no_changes: !!st.no_changes } : null;
        if (st) navigate(location.pathname, { replace: true, state: null });
        const put = (doc) => setThesis(doc && genNotes.current ? { ...doc, ...genNotes.current } : doc);
        setLoading(true);
        thesisGet(id)
            .then((d) => {
                if (!alive) return;
                put(d); setError(null);
                // The drivers' theses are still being generated: follow them.
                if (d?.type === "company" && d.inflight_count > 0) {
                    thesisWaitPlanDone(id, { onProgress: (doc) => { if (alive && doc) put(doc); } }).catch(() => {});
                }
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.detail || "No se pudo cargar la tesis"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const generateContra = async () => {
        setGeneratingContra(true);
        try {
            const contra = await thesisGenerateContra(id);
            setThesis((prev) => ({ ...prev, contra }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo generar la contratesis");
        } finally {
            setGeneratingContra(false);
        }
    };

    const isTendencia = thesis?.type === "tendencia";

    // «Generar las que faltan»: the drivers' theses still missing, queued; then follow
    // them until every one lands.
    const generatePlan = async (companyId) => {
        if (!companyId) return;
        setGeneratingPlan(true);
        try {
            const res = await thesisGeneratePlan(companyId);
            toast.success(res.count === 1 ? "Generando la tesis que faltaba." : `Generando las ${res.count} tesis que faltaban (en cola).`);
            const fresh = await thesisGet(companyId);
            setThesis(fresh);
            thesisWaitPlanDone(companyId, {
                onProgress: (doc) => { if (doc) setThesis(doc); },
            }).catch(() => {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo generar el plan.");
        } finally {
            setGeneratingPlan(false);
        }
    };

    const refresh = async () => {
        // Full-scope refresh (no thesis_id / no ticker): re-fetches fundamentals for
        // every ticker in the user's saved theses and recomputes every TAM Score in
        // place. Guarantees coherence between this page, the dashboard treemap and
        // the standalone company page when the user presses the refresh icon.
        const res = await thesisRefreshRun({});
        if (res?.thesis) setThesis(res.thesis);
        else { const d = await thesisGet(id); setThesis(d); }
    };

    return (
        <div data-testid="thesis-detail-page">
            <div className="flex items-start justify-end gap-3 mb-4">
                {user && thesis && !isTendencia && <RefreshButton onRefresh={refresh} testid="thesis-detail-refresh" />}
            </div>
            {loading && (
                <div className="flex items-center gap-2 text-[#4A4A4A] py-10" data-testid="thesis-detail-loading">
                    <Loader2 size={16} className="animate-spin" /> Cargando tesis…
                </div>
            )}
            {error && !loading && (
                <div className="border border-[#B32A22]/40 bg-white p-6 text-[#B32A22]" data-testid="thesis-detail-error">{error}</div>
            )}
            {thesis && !loading && (
                isTendencia ? (
                    <TendenciaResult
                        tendencia={thesis}
                        saved
                        canSave={false}
                        onDevelopCompany={(ticker) => ticker && navigate(`/thesis?company=${encodeURIComponent(ticker)}`)}
                    />
                ) : (
                    <>
                    {user && thesis.type === "company" && thesis.company?.ticker && (
                        <SharedGeneration
                            ticker={thesis.company.ticker}
                            onUpdated={async (res) => {
                                if (res?.company_id && res.company_id !== id) { navigate(`/thesis/${res.company_id}`); return; }
                                try { setThesis(await thesisGet(id)); } catch { /* keep */ }
                            }}
                        />
                    )}
                    <ThesisResult
                        thesis={thesis}
                        canGenerateContra={!!user}
                        onGenerateContra={generateContra}
                        generatingContra={generatingContra}
                        onGeneratePlan={generatePlan}
                        generatingPlan={generatingPlan}
                    />
                    </>
                )
            )}
        </div>
    );
}
